// Painel de monitoramento para TV (1920×1080). Somente leitura; atualiza a cada 15 s.
// Acesso por sessão logada (quem lê analytics) ou por token de exibição (?token=disp_...).
import { useEffect, useMemo, useState } from 'react';

const REFRESH_MS = 15_000;
const STALE_MS = 60_000;
const TZ = 'America/Sao_Paulo';
const TOKEN_KEY = 'mcc_display_token';

// Cores de status fixas (sempre acompanhadas de ícone + rótulo) e série única no azul oficial.
const STATUS = {
  good: { color: '#0ca30c', icon: '✓' },
  warning: { color: '#fab219', icon: '▲' },
  serious: { color: '#ec835a', icon: '◆' },
  critical: { color: '#d03b3b', icon: '●' },
  info: { color: '#41b3e2', icon: '•' },
  neutral: { color: '#9aa0c8', icon: '·' },
} as const;
type Tone = keyof typeof STATUS;
const SERIES = '#41b3e2';

interface Wall {
  generatedAt: string;
  env: string;
  llm: string;
  killSwitch: { active: boolean; reason: string | null; at: string | null };
  kpis: {
    requestsWeek: number;
    pendingApproval: number;
    scheduled7d: number;
    publishedWeek: number;
    needsAttention: number;
    requestsBlocked: number;
    overdueSla: number;
    aiCostMonthUsd: number;
    aiBudgetMonthUsd: number;
    activeAgents: number;
    totalAgents: number;
  };
  funnel: { key: string; label: string; count: number }[];
  approvals: { id: string; title: string; channel: string; risk: string; requiredRoles: string[]; waitingHours: number; scheduledAt: string | null }[];
  awaitingManual: { id: string; title: string; channel: string; since: string }[];
  upcoming: { id: string; title: string; channel: string; state: string; scheduledAt: string }[];
  agents: { id: string; name: string; status: string; phase: string; model: string; runsToday: number; failuresToday: number; runningNow: boolean; lastRunAt: string | null; lastStatus: string | null; costMonthUsd: number; budgetMonthUsd: number }[];
  costDaily: { day: string; usd: number }[];
  activity: { at: string; text: string; tone: Tone }[];
  radar: { title: string; topic: string | null; relevance: number | null; source: string | null }[];
  health: { queueDepth: number; runningJobs: number; failedJobs24h: number; lastJobFinishedAt: string | null; integrations: { name: string; status: string }[]; publishMode: string };
}

function readToken(): string | null {
  const url = new URL(window.location.href);
  const fromUrl = url.searchParams.get('token');
  if (fromUrl) {
    try {
      localStorage.setItem(TOKEN_KEY, fromUrl);
      url.searchParams.delete('token');
      window.history.replaceState(null, '', url.toString()); // tira o token da barra de endereço
    } catch {
      /* armazenamento indisponível: segue com o token da URL */
    }
    return fromUrl;
  }
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

const hhmm = (iso: string) => new Intl.DateTimeFormat('pt-BR', { timeZone: TZ, hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
const dayTime = (iso: string) => new Intl.DateTimeFormat('pt-BR', { timeZone: TZ, weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
function ago(iso: string | null, now: number) {
  if (!iso) return '—';
  const s = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  if (s < 60) return `há ${s}s`;
  if (s < 3600) return `há ${Math.round(s / 60)} min`;
  if (s < 86400) return `há ${Math.round(s / 3600)} h`;
  return `há ${Math.round(s / 86400)} d`;
}
const usd = (v: number) => `US$ ${v.toFixed(2)}`;

function StatusTag({ tone, label }: { tone: Tone; label: string }) {
  const s = STATUS[tone];
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-[0.95em] font-semibold" style={{ color: s.color }}>
      <span aria-hidden>{s.icon}</span>
      {label}
    </span>
  );
}

function Panel({ title, right, children, className = '' }: { title: string; right?: React.ReactNode; children: React.ReactNode; className?: string }) {
  return (
    <section className={`flex min-h-0 flex-col rounded-xl border border-white/10 bg-white/[0.04] p-4 ${className}`}>
      <div className="mb-3 flex items-baseline justify-between gap-2">
        <h2 className="text-[1.05rem] font-semibold uppercase tracking-wider text-[#9fd8f0]">{title}</h2>
        {right && <div className="text-sm text-white/60">{right}</div>}
      </div>
      <div className="min-h-0 flex-1 overflow-hidden">{children}</div>
    </section>
  );
}

function Kpi({ label, value, hint, tone }: { label: string; value: string | number; hint?: React.ReactNode; tone?: Tone }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.06] px-4 py-3">
      <div className="text-sm font-medium uppercase tracking-wide text-white/70">{label}</div>
      <div className="mt-1 flex items-baseline gap-2">
        <span className="text-5xl font-bold tabular-nums text-white">{value}</span>
        {tone && tone !== 'neutral' && <span style={{ color: STATUS[tone].color }} className="text-2xl" aria-hidden>{STATUS[tone].icon}</span>}
      </div>
      {hint && <div className="mt-1 text-sm text-white/60">{hint}</div>}
    </div>
  );
}

function agentState(a: Wall['agents'][number]): { tone: Tone; label: string } {
  if (a.status !== 'ACTIVE') return { tone: 'neutral', label: `Inativo · ${a.phase.replace('_', ' ').replace('FASE', 'Fase')}` };
  if (a.runningNow) return { tone: 'info', label: 'Trabalhando' };
  if (a.failuresToday > 0) return { tone: 'critical', label: `${a.failuresToday} falha(s) hoje` };
  if (a.budgetMonthUsd > 0 && a.costMonthUsd >= a.budgetMonthUsd * 0.8) return { tone: 'warning', label: 'Orçamento ≥ 80%' };
  return { tone: 'good', label: 'Pronto' };
}

export function Wall() {
  const [token] = useState(readToken);
  const [data, setData] = useState<Wall | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastOk, setLastOk] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await fetch('/api/wall', { headers: token ? { 'x-display-token': token } : {}, credentials: 'same-origin', cache: 'no-store' });
        if (!res.ok) throw new Error(res.status === 401 ? 'Token de exibição inválido ou revogado. Gere um novo em Admin → Painéis de TV.' : `Erro ${res.status}`);
        const json = (await res.json()) as Wall;
        if (!alive) return;
        setData(json);
        setLastOk(Date.now());
        setError(null);
      } catch (e) {
        if (alive) setError((e as Error).message);
      }
    };
    void load();
    const t = setInterval(load, REFRESH_MS);
    const c = setInterval(() => setNow(Date.now()), 1000);
    // Mantém a tela acesa quando o navegador permite (Wake Lock).
    (navigator as Navigator & { wakeLock?: { request(t: 'screen'): Promise<unknown> } }).wakeLock?.request('screen').catch(() => undefined);
    return () => {
      alive = false;
      clearInterval(t);
      clearInterval(c);
    };
  }, [token]);

  const stale = lastOk !== null && now - lastOk > STALE_MS;
  const clock = useMemo(() => new Intl.DateTimeFormat('pt-BR', { timeZone: TZ, hour: '2-digit', minute: '2-digit', second: '2-digit' }).format(new Date(now)), [now]);
  const date = useMemo(() => new Intl.DateTimeFormat('pt-BR', { timeZone: TZ, weekday: 'long', day: '2-digit', month: 'long' }).format(new Date(now)), [now]);

  if (!data) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#0f1038] p-8 text-center text-2xl text-white">
        <div>
          <img src="/brand/3s-logo-negativo.svg" alt="3S Networks" className="mx-auto mb-6 h-24" />
          {error ?? 'Carregando painel…'}
        </div>
      </div>
    );
  }

  const k = data.kpis;
  const funnelMax = Math.max(1, ...data.funnel.map((f) => f.count));
  const costMax = Math.max(0.0001, ...data.costDaily.map((d) => d.usd));
  const budgetPct = k.aiBudgetMonthUsd ? Math.min(100, (k.aiCostMonthUsd / k.aiBudgetMonthUsd) * 100) : 0;
  const active = data.agents.filter((a) => a.status === 'ACTIVE');
  const planned = data.agents.filter((a) => a.status !== 'ACTIVE');

  return (
    <div className="flex h-screen flex-col gap-3 overflow-hidden bg-[#0f1038] p-4 text-white" style={{ fontSize: 'clamp(13px, 0.85vw, 18px)' }}>
      {/* Cabeçalho */}
      <header className="flex items-center gap-6">
        <img src="/brand/3s-logo-negativo.svg" alt="3S Networks" className="h-16 w-auto" />
        <div className="flex-1">
          <h1 className="text-3xl font-bold">Marketing AI Command Center</h1>
          <div className="mt-1 flex flex-wrap gap-4 text-base text-white/70">
            <span>Ambiente: <strong className="text-white">{data.env}</strong></span>
            <span>IA: <strong className="text-white">{data.llm === 'anthropic' ? 'Claude (real)' : 'simulada'}</strong></span>
            <span>Publicação: <strong className="text-white">{data.health.publishMode}</strong></span>
            <span>Agentes ativos: <strong className="text-white">{k.activeAgents}/{k.totalAgents}</strong></span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-5xl font-bold tabular-nums">{clock}</div>
          <div className="text-base capitalize text-white/70">{date} · São Paulo</div>
          <div className="mt-1 text-sm">
            {error || stale ? (
              <StatusTag tone="critical" label={`Sem atualização ${ago(lastOk ? new Date(lastOk).toISOString() : null, now)}`} />
            ) : (
              <StatusTag tone="good" label={`Ao vivo · atualizado ${ago(data.generatedAt, now)}`} />
            )}
          </div>
        </div>
      </header>

      {data.killSwitch.active && (
        <div role="alert" className="rounded-xl bg-[#d03b3b] px-6 py-3 text-center text-2xl font-bold">
          ● KILL SWITCH ATIVO — todos os envios externos pausados{data.killSwitch.reason ? `: ${data.killSwitch.reason}` : ''}
        </div>
      )}

      {data.awaitingManual.length > 0 && (
        <div role="status" className="flex flex-wrap items-center gap-x-6 gap-y-1 rounded-xl border-2 border-[#fab219] bg-[#fab219]/15 px-5 py-2 text-xl">
          <span className="font-bold" style={{ color: STATUS.warning.color }}>
            ▲ Publicar agora ({data.awaitingManual.length})
          </span>
          {data.awaitingManual.slice(0, 4).map((m) => (
            <span key={m.id} className="text-white">
              {m.title} <span className="text-base text-white/60">· {m.id} · {ago(m.since, now)}</span>
            </span>
          ))}
        </div>
      )}

      {/* Indicadores */}
      <div className="grid grid-cols-7 gap-3">
        <Kpi label="Demandas na semana" value={k.requestsWeek} />
        <Kpi label="Aguardando aprovação" value={k.pendingApproval} tone={k.pendingApproval ? 'warning' : 'neutral'} hint={k.pendingApproval ? 'decisão humana pendente' : 'fila vazia'} />
        <Kpi label="Precisam de atenção" value={k.needsAttention} tone={k.needsAttention ? 'serious' : 'neutral'} hint={k.requestsBlocked ? `${k.requestsBlocked} demanda(s) parada(s) + peças` : 'ajuste, bloqueio ou falha'} />
        <Kpi label="SLA vencido" value={k.overdueSla} tone={k.overdueSla ? 'critical' : 'neutral'} hint="demandas abertas" />
        <Kpi label="Agendadas · 7 dias" value={k.scheduled7d} />
        <Kpi label="Publicadas na semana" value={k.publishedWeek} hint={data.health.publishMode === 'SIMULADO' ? 'em modo simulado' : undefined} />
        <div className="rounded-xl border border-white/10 bg-white/[0.06] px-4 py-3">
          <div className="text-sm font-medium uppercase tracking-wide text-white/70">Custo de IA no mês</div>
          <div className="mt-1 text-4xl font-bold tabular-nums">{usd(k.aiCostMonthUsd)}</div>
          <div className="mt-2 h-2.5 w-full rounded-full bg-white/10" role="img" aria-label={`${budgetPct.toFixed(0)}% do teto de ${usd(k.aiBudgetMonthUsd)}`}>
            <div className="h-2.5 rounded-full" style={{ width: `${Math.max(1, budgetPct)}%`, background: budgetPct >= 80 ? STATUS.warning.color : SERIES }} />
          </div>
          <div className="mt-1 text-sm text-white/60">{budgetPct.toFixed(1)}% do teto ({usd(k.aiBudgetMonthUsd)})</div>
        </div>
      </div>

      {/* Corpo */}
      <div className="grid min-h-0 flex-1 grid-cols-12 gap-3">
        <div className="col-span-4 flex min-h-0 flex-col gap-3">
          <Panel title="Esteira de conteúdo" right="peças por etapa">
            <ul className="space-y-2.5">
              {data.funnel.map((f) => (
                <li key={f.key} title={`${f.label}: ${f.count}`}>
                  <div className="mb-1 flex justify-between text-base">
                    <span className={f.key === 'atencao' && f.count ? 'font-semibold' : 'text-white/85'}>
                      {f.key === 'atencao' && f.count > 0 && <span style={{ color: STATUS.serious.color }} aria-hidden>◆ </span>}
                      {f.label}
                    </span>
                    <span className="font-bold tabular-nums">{f.count}</span>
                  </div>
                  <div className="h-3 rounded-r-[4px] bg-white/5">
                    <div className="h-3 rounded-r-[4px]" style={{ width: `${(f.count / funnelMax) * 100}%`, minWidth: f.count ? 6 : 0, background: f.key === 'atencao' ? STATUS.serious.color : SERIES }} />
                  </div>
                </li>
              ))}
            </ul>
          </Panel>
          <Panel title="Fila de aprovação humana" right={`${data.approvals.length} na fila`} className="flex-1">
            {data.approvals.length ? (
              <ul className="space-y-2 overflow-hidden">
                {data.approvals.map((a) => {
                  const tone: Tone = a.waitingHours > 48 ? 'critical' : a.waitingHours > 24 ? 'warning' : 'good';
                  return (
                    <li key={a.id} className="rounded-lg bg-white/[0.05] px-3 py-2">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="truncate text-base font-semibold">{a.title}</span>
                        <StatusTag tone={tone} label={`${a.waitingHours.toFixed(0)} h`} />
                      </div>
                      <div className="text-sm text-white/65">
                        {a.id} · risco {a.risk} · exige {a.requiredRoles.map((r) => r.replace('MARKETING_SPONSOR', 'Sponsor').replace('TECH_REVIEWER', 'Técnico').replace('SALES_MANAGER', 'Comercial').replace('FINANCE_LEGAL', 'Jurídico')).join(', ')}
                        {a.scheduledAt && ` · publica ${dayTime(a.scheduledAt)}`}
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-lg text-white/60">Nenhuma peça aguardando aprovação.</p>
            )}
          </Panel>
        </div>

        <div className="col-span-5 flex min-h-0 flex-col gap-3">
          <Panel title="Agentes de IA" right={<span>{active.filter((a) => a.runningNow).length} trabalhando agora</span>}>
            <div className="grid grid-cols-3 gap-2">
              {active.map((a) => {
                const st = agentState(a);
                const pct = a.budgetMonthUsd ? Math.min(100, (a.costMonthUsd / a.budgetMonthUsd) * 100) : 0;
                return (
                  <div key={a.id} className={`rounded-lg border px-3 py-2 ${a.runningNow ? 'border-[#41b3e2] bg-[#41b3e2]/10' : 'border-white/10 bg-white/[0.05]'}`} title={`${a.name} · ${a.model}`}>
                    <div className="flex items-baseline justify-between gap-1">
                      <span className="text-lg font-bold">{a.id}</span>
                      <StatusTag tone={st.tone} label={st.label} />
                    </div>
                    <div className="truncate text-sm text-white/75">{a.name}</div>
                    <div className="mt-1 flex justify-between text-xs text-white/60">
                      <span>{a.runsToday} execução(ões) hoje</span>
                      <span>{a.lastRunAt ? ago(a.lastRunAt, now) : 'sem execuções'}</span>
                    </div>
                    {a.budgetMonthUsd > 0 && (
                      <div className="mt-1 h-1.5 rounded-full bg-white/10" role="img" aria-label={`Custo ${usd(a.costMonthUsd)} de ${usd(a.budgetMonthUsd)}`}>
                        <div className="h-1.5 rounded-full" style={{ width: `${Math.max(pct ? 2 : 0, pct)}%`, background: pct >= 80 ? STATUS.warning.color : SERIES }} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="mt-2 text-sm text-white/55">
              Planejados (inativos): {planned.map((a) => a.id).join(' · ')}
            </div>
          </Panel>
          <div className="grid min-h-0 flex-1 grid-cols-2 gap-3">
            <Panel title="Próximas publicações">
              {data.upcoming.length ? (
                <ul className="space-y-2">
                  {data.upcoming.map((u) => (
                    <li key={u.id}>
                      <div className="text-base font-semibold capitalize">{dayTime(u.scheduledAt)}</div>
                      <div className="truncate text-sm text-white/75">
                        {u.title} · {u.channel === 'linkedin_company' ? 'LinkedIn 3S' : u.channel}
                      </div>
                      <StatusTag tone={u.state === 'SCHEDULED' ? 'good' : 'warning'} label={u.state === 'SCHEDULED' ? 'Aprovado e agendado' : 'Ainda sem aprovação'} />
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-base text-white/60">Nada previsto para os próximos 7 dias.</p>
              )}
            </Panel>
            <Panel title="Custo de IA · 14 dias" right={`máx. ${usd(costMax)}/dia`}>
              <div className="flex h-[calc(100%-1.5rem)] min-h-[6rem] items-end gap-[2px]" role="img" aria-label="Custo diário de IA nos últimos 14 dias">
                {data.costDaily.map((d) => (
                  <div key={d.day} className="flex h-full flex-1 flex-col justify-end" title={`${d.day.slice(8)}/${d.day.slice(5, 7)}: ${usd(d.usd)}`}>
                    <div className="rounded-t-[4px]" style={{ height: `${(d.usd / costMax) * 100}%`, minHeight: d.usd ? 3 : 0, background: SERIES }} />
                  </div>
                ))}
              </div>
              <div className="mt-1 flex justify-between text-xs text-white/55">
                <span>{data.costDaily[0].day.slice(8)}/{data.costDaily[0].day.slice(5, 7)}</span>
                <span>hoje</span>
              </div>
            </Panel>
          </div>
        </div>

        <div className="col-span-3 flex min-h-0 flex-col gap-3">
          <Panel title="Atividade ao vivo" className="flex-[3]">
            <ol className="space-y-1.5 overflow-hidden">
              {data.activity.map((e, i) => (
                <li key={`${e.at}-${i}`} className="flex gap-2 text-[0.95rem] leading-snug">
                  <span className="w-12 shrink-0 tabular-nums text-white/50">{hhmm(e.at)}</span>
                  <span aria-hidden style={{ color: STATUS[e.tone].color }}>{STATUS[e.tone].icon}</span>
                  <span className={e.tone === 'critical' ? 'font-semibold' : 'text-white/85'}>{e.text}</span>
                </li>
              ))}
            </ol>
          </Panel>
          <Panel title="Radar de mercado" className="flex-[2]">
            {data.radar.length ? (
              <ul className="space-y-1.5">
                {data.radar.map((r, i) => (
                  <li key={i} className="flex gap-2 text-[0.95rem] leading-snug">
                    <span className="w-8 shrink-0 text-right font-bold tabular-nums" style={{ color: SERIES }}>{r.relevance}</span>
                    <span className="line-clamp-2 text-white/85">{r.title} <span className="text-white/50">· {r.topic}</span></span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-base text-white/60">Sem itens nos últimos 3 dias. O radar roda em dias úteis às 08:30.</p>
            )}
          </Panel>
        </div>
      </div>

      {/* Saúde operacional */}
      <footer className="flex flex-wrap items-center gap-x-6 gap-y-1 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-2 text-base">
        <span className="font-semibold uppercase tracking-wider text-[#9fd8f0]">Saúde</span>
        <StatusTag tone={data.health.queueDepth > 10 ? 'warning' : 'good'} label={`Fila: ${data.health.queueDepth}`} />
        <span className="text-white/80">Em execução: {data.health.runningJobs}</span>
        <StatusTag tone={data.health.failedJobs24h ? 'critical' : 'good'} label={`Falhas 24 h: ${data.health.failedJobs24h}`} />
        <span className="text-white/80">Último job: {ago(data.health.lastJobFinishedAt, now)}</span>
        <span className="ml-auto flex flex-wrap gap-x-4 text-sm">
          {data.health.integrations.map((i) => (
            <StatusTag key={i.name} tone={i.status === 'CONNECTED' ? 'good' : i.status === 'SIMULATED' ? 'info' : i.status === 'ERROR' ? 'critical' : 'neutral'} label={i.name.replace(/ \(.+\)$/, '')} />
          ))}
        </span>
      </footer>
    </div>
  );
}
